import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Mic, Gamepad2, Trophy, Rocket, Joystick, Baby, Shield, Heart,
  Play, Plus, TrendingUp, Star, GraduationCap, Wand2, Palette, BarChart3,
  Wallet, ShoppingBag, Users, CreditCard, Bell, FileCheck, BookOpen, ClipboardList,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMode } from "@/context/ModeContext";
import { MascotBubble } from "@/components/Mascot";
import { StatCard, GameCard, Spinner, EmptyState } from "@/components/Bits";
import OnboardingCard from "@/components/OnboardingCard";
import DailyQuests from "@/components/DailyQuests";
import RaceWidget from "@/components/RaceWidget";
import NextMilestone from "@/components/NextMilestone";
import CreatorWrapped from "@/components/CreatorWrapped";
import RoomInvitesBanner from "@/components/RoomInvitesBanner";
import ResumeRoomCard from "@/components/ResumeRoomCard";
import PayoutReadyBanner from "@/components/PayoutReadyBanner";
import FirstRunWelcome from "@/components/FirstRunWelcome";
import FirstWinNudge from "@/components/FirstWinNudge";
import ReferralCard from "@/components/ReferralCard";
import MashupOfTheDay from "@/components/MashupOfTheDay";
import MashupChallenge from "@/components/MashupChallenge";
import RobotReportCard from "@/components/RobotReportCard";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isKid, setMode } = useMode();
  const [profile, setProfile] = useState(null);
  const [games, setGames] = useState([]);
  const [extra, setExtra] = useState({});

  useEffect(() => {
    if (user?.role === "kid" || user?.role === "student") setMode("kid");
    Promise.all([api.get("/me/profile"), api.get("/games").catch(() => ({ data: [] }))])
      .then(([p, g]) => { setProfile(p.data); setGames(g.data); });
    if (user?.role === "creator") {
      api.get("/creator/analytics").then((r) => setExtra((e) => ({ ...e, analytics: r.data }))).catch(() => {});
    } else if (user?.role === "parent") {
      Promise.all([
        api.get("/parent/dashboard").catch(() => ({ data: {} })),
        api.get("/parent/purchase-requests").catch(() => ({ data: [] })),
        api.get("/parent/monetization/pending").catch(() => ({ data: [] })),
      ]).then(([d, pr, mr]) => setExtra((e) => ({ ...e, parent: d.data, purchases: pr.data.length, monet: mr.data.length })));
    }
    // eslint-disable-next-line
  }, []);

  if (!profile) return <Spinner />;
  const stats = profile.stats;
  const kidView = isKid || user.role === "kid" || user.role === "student";
  const recent = games.slice(0, 4);
  const open = (g) => navigate(`/app/build/${g.id}`);

  // ── KID / STUDENT ───────────────────────────────────────────────
  if (kidView && ["kid", "student", "teen", "creator"].includes(user.role)) {
    const waiting = games.filter((g) => g.status === "pending_approval").length;
    return (
      <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="dashboard-kid">
        <RoomInvitesBanner />
        <ResumeRoomCard />
        <div className="mb-6"><MascotBubble message={`Hi ${user.name}! Ready to make something awesome today?`} /></div>
        <div className="grid md:grid-cols-3 gap-3 mb-6">
          <button onClick={() => navigate("/app/create")} data-testid="kid-create-big"
            className="md:col-span-2 rounded-3xl p-8 text-white text-center bg-gradient-to-br from-[#7c3aed] via-[#1f6feb] to-[#3b9eff] brand-glow card-hover animate-rise">
            <Sparkles className="w-12 h-12 mx-auto mb-2" />
            <div className="font-display text-3xl font-extrabold">Create a Game!</div>
            <div className="opacity-90 mt-1">Speak or type your idea and watch the magic ✨</div>
          </button>
          <button onClick={() => navigate("/app/create")} data-testid="kid-voice-btn"
            className="rounded-3xl p-8 text-center bg-[hsl(var(--card))] border border-[hsl(var(--border))] card-hover animate-rise flex flex-col items-center justify-center">
            <div className="w-14 h-14 rounded-full grid place-items-center bg-[hsl(var(--primary))]/15 mb-2 animate-pulse-glow"><Mic className="w-7 h-7 text-[hsl(var(--primary))]" /></div>
            <div className="font-display text-xl font-extrabold">Use your voice</div>
            <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Just say your idea out loud</div>
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <StatCard label="Games made" value={stats.games_created} icon={Gamepad2} delay={1} />
          <StatCard label="Published" value={stats.games_published} icon={Rocket} accent="#10b981" delay={2} />
          <StatCard label="Badges" value={stats.achievements} icon={Trophy} accent="#f59e0b" delay={3} />
          <StatCard label="Level" value={stats.level} icon={Star} accent="#a855f7" delay={4} />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <ActionCard icon={Gamepad2} title="My Games" text="See everything you've made" onClick={() => navigate("/app/games")} testid="kid-tile-games" />
          <ActionCard icon={Trophy} title="My Rewards" text="Badges, stars & XP" onClick={() => navigate("/app/achievements")} testid="kid-tile-rewards" />
          <ActionCard icon={Joystick} title="Play Games" text="Fun games to play" onClick={() => navigate("/app/play")} testid="kid-tile-play" />
          <ActionCard icon={Sparkles} title="Ask Pixel" text="Your AI helper buddy" onClick={() => window.dispatchEvent(new Event("gv-open-assistant"))} testid="kid-tile-ask" />
        </div>

        <MashupOfTheDay />

        <MashupChallenge compact />

        {waiting > 0 && (
          <div className="mb-8 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5 flex items-center gap-3" data-testid="kid-waiting-approval">
            <Bell className="w-7 h-7 text-amber-500 shrink-0" />
            <div className="flex-1"><div className="font-bold">{waiting} game{waiting > 1 ? "s" : ""} waiting for a grown-up</div>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Ask your parent to approve so the world can play it!</p></div>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => navigate("/app/games")}>View</Button>
          </div>
        )}

        <SectionHead title="My recent games" onAll={() => navigate("/app/games")} />
        {recent.length === 0 ? (
          <EmptyState emoji="🎨" title="No games yet!" text="Tap Create a Game to make your first one." action={<Button onClick={() => navigate("/app/create")} className="rounded-full"><Plus className="w-4 h-4" /> Create</Button>} />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {recent.map((g) => <GameCard key={g.id} game={{ ...g, world: g.config?.world }} onClick={() => open(g)} badge={g.status === "published" ? "LIVE" : g.status === "pending_approval" ? "WAITING" : "DRAFT"} />)}
          </div>
        )}
      </div>
    );
  }

  // ── PARENT ──────────────────────────────────────────────────────
  if (user.role === "parent") {
    const p = extra.parent || {};
    return (
      <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="dashboard-parent">
        <RoomInvitesBanner />
        <ResumeRoomCard />
        <h1 className="font-display text-3xl font-extrabold mb-1">Welcome, {user.name} 👋</h1>
        <p className="text-[hsl(var(--muted-foreground))] mb-6">Manage your family's safe game creation.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <StatCard label="Child accounts" value={p.children_count ?? 0} icon={Baby} delay={1} />
          <StatCard label="Approvals waiting" value={p.pending_approvals ?? 0} icon={FileCheck} accent="#f59e0b" delay={2} />
          <StatCard label="Purchase requests" value={extra.purchases ?? 0} icon={CreditCard} accent="#ef4444" delay={3} />
          <StatCard label="Monetisation reqs" value={extra.monet ?? 0} icon={Wallet} accent="#a855f7" delay={4} />
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <ActionCard icon={Baby} title="Children & Approvals" text="Accounts, publishing & purchase approvals" onClick={() => navigate("/app/parent")} testid="parent-tile-children" />
          <ActionCard icon={Shield} title="Safety & Controls" text="Playtime, privacy & spending limits" onClick={() => navigate("/app/parent")} testid="parent-tile-safety" />
          <ActionCard icon={Joystick} title="Browse Arcade" text="See kid-safe games" onClick={() => navigate("/app/play")} testid="parent-tile-arcade" />
        </div>
        <MascotBubble message="I keep every game safe and ask for your approval before kids publish, buy or monetise. You're in control!" />
      </div>
    );
  }

  // ── ADMIN ───────────────────────────────────────────────────────
  if (user.role === "admin") {
    return (
      <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="dashboard-admin">
        <RoomInvitesBanner />
        <ResumeRoomCard />
        <h1 className="font-display text-3xl font-extrabold mb-1">Admin Control Center 🛡️</h1>
        <p className="text-[hsl(var(--muted-foreground))] mb-6">Platform oversight, moderation & analytics.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <ActionCard icon={Shield} title="Moderation & Users" text="Users, reports & moderation queue" onClick={() => navigate("/app/admin")} testid="admin-tile-moderation" />
          <ActionCard icon={Wallet} title="Monetisation Approvals" text="Review premium packs & payouts" onClick={() => navigate("/app/admin")} testid="admin-tile-monet" />
          <ActionCard icon={BarChart3} title="Platform Analytics" text="Usage, storage & revenue" onClick={() => navigate("/app/admin")} testid="admin-tile-analytics" />
          <ActionCard icon={Joystick} title="Arcade" text="Featured & trending games" onClick={() => navigate("/app/play")} testid="admin-tile-arcade" />
          <ActionCard icon={ShoppingBag} title="Marketplace" text="Listings & moderation" onClick={() => navigate("/app/marketplace")} testid="admin-tile-market" />
          <ActionCard icon={Sparkles} title="Create Game" text="Test the AI builder" onClick={() => navigate("/app/create")} testid="admin-tile-create" />
        </div>
      </div>
    );
  }

  // ── EDUCATOR / SCHOOL ───────────────────────────────────────────
  if (user.role === "educator") {
    return (
      <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="dashboard-school">
        <RoomInvitesBanner />
        <ResumeRoomCard />
        <h1 className="font-display text-3xl font-extrabold mb-1">Welcome, {user.name} 🎓</h1>
        <p className="text-[hsl(var(--muted-foreground))] mb-6">Run your classroom and inspire young game creators.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <ActionCard icon={Users} title="Classes & Students" text="Manage classrooms & invites" onClick={() => navigate("/app/school")} testid="school-tile-classes" />
          <ActionCard icon={ClipboardList} title="Assignments" text="Create & review student work" onClick={() => navigate("/app/school")} testid="school-tile-assignments" />
          <ActionCard icon={BookOpen} title="Lesson Templates" text="Ready-made game lessons" onClick={() => navigate("/app/school")} testid="school-tile-lessons" />
          <ActionCard icon={GraduationCap} title="Classroom Arcade" text="Private showcase of class games" onClick={() => navigate("/app/school")} testid="school-tile-arcade" />
          <ActionCard icon={Sparkles} title="Create a Demo" text="Build a lesson game with AI" onClick={() => navigate("/app/create")} testid="school-tile-create" />
          <ActionCard icon={Joystick} title="Browse Arcade" text="Safe games for inspiration" onClick={() => navigate("/app/play")} testid="school-tile-browse" />
        </div>
        <MascotBubble message="Set up a class, add students, create assignments, and approve their games into a private class showcase. Everything stays school-safe!" />
      </div>
    );
  }

  // ── PLAYER ──────────────────────────────────────────────────────
  if (user.role === "player") {
    return (
      <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="dashboard-player">
        <RoomInvitesBanner />
        <ResumeRoomCard />
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-extrabold">Hey {user.name} 🕹️</h1>
            <p className="text-[hsl(var(--muted-foreground))] mt-1">Jump back into the Arcade.</p>
          </div>
          <Button onClick={() => navigate("/app/play")} data-testid="player-arcade-btn" className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]"><Joystick className="w-4 h-4" /> Play Games</Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <StatCard label="Games played" value={stats.games_played} icon={Play} delay={1} />
          <StatCard label="Badges" value={stats.achievements} icon={Trophy} accent="#f59e0b" delay={2} />
          <StatCard label="Day streak" value={1} icon={TrendingUp} accent="#10b981" delay={3} />
          <StatCard label="Level" value={stats.level} icon={Star} accent="#a855f7" delay={4} />
        </div>
        <DailyQuests />
        <div className="grid sm:grid-cols-3 gap-4">
          <ActionCard icon={Joystick} title="GameView Arcade" text="Discover trending games" onClick={() => navigate("/app/play")} />
          <ActionCard icon={Trophy} title="Achievements" text="Track your badges & XP" onClick={() => navigate("/app/achievements")} />
          <ActionCard icon={Heart} title="Your Profile" text="Stats & favourites" onClick={() => navigate("/app/profile")} />
        </div>
      </div>
    );
  }

  // ── CREATOR (studio) ────────────────────────────────────────────
  const a = extra.analytics;
  const drafts = Math.max(0, stats.games_created - stats.games_published);
  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="dashboard-creator">
      <FirstRunWelcome />
      <RoomInvitesBanner />
        <ResumeRoomCard />
      <PayoutReadyBanner />
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Welcome back, {user.name}</h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-1">Your creator command center.</p>
        </div>
        <Button onClick={() => navigate("/app/create")} data-testid="dash-create-btn" className="rounded-full bg-gradient-to-r from-[#7c3aed] to-[#3b9eff]"><Sparkles className="w-4 h-4" /> Create Game</Button>
      </div>

      <FirstWinNudge />
      <OnboardingCard />

      <CreatorWrapped />

      <ReferralCard />

      <RaceWidget />

      <NextMilestone />

      <DailyQuests />

      <MashupOfTheDay />

      <MashupChallenge compact />

      <RobotReportCard />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <StatCard label="Published" value={stats.games_published} icon={Rocket} accent="#10b981" delay={1} />
        <StatCard label="Drafts" value={drafts} icon={Gamepad2} delay={2} />
        <StatCard label="Total plays" value={stats.total_plays} icon={TrendingUp} accent="#f59e0b" delay={3} />
        <StatCard label="Followers" value={a?.followers ?? 0} icon={Users} accent="#3b9eff" delay={4} />
        <StatCard label="Avg rating" value={a?.avg_rating ?? 0} icon={Star} accent="#eab308" delay={5} />
        <StatCard label="Level" value={stats.level} icon={Trophy} accent="#a855f7" delay={5} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
        <ActionCard icon={Wand2} title="AI Level Generator" text="Up to 1000 levels" onClick={() => navigate("/app/games")} testid="creator-tile-levels" />
        <ActionCard icon={Palette} title="Asset Studio" text="AI art & characters" onClick={() => navigate("/app/assets")} testid="creator-tile-assets" />
        <ActionCard icon={BarChart3} title="Creator Dashboard" text="Plays, conversion, revenue" onClick={() => navigate("/app/creator-dashboard")} testid="creator-tile-dashboard" />
        <ActionCard icon={Wallet} title="Earnings" text="Sales & payouts" onClick={() => navigate("/app/earnings")} testid="creator-tile-earnings" />
        <ActionCard icon={Rocket} title="Creator Hub" text="Analytics & insights" onClick={() => navigate("/app/creator")} testid="creator-tile-hub" />
        <ActionCard icon={ShoppingBag} title="Marketplace" text="Sell & buy assets" onClick={() => navigate("/app/marketplace")} testid="creator-tile-market" />
        <ActionCard icon={Joystick} title="Play Games" text="Play & discover community games" onClick={() => navigate("/app/play")} testid="creator-tile-arcade" />
        <ActionCard icon={Trophy} title="Events & Jams" text="Compete & win" onClick={() => navigate("/app/arena")} testid="creator-tile-arena" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SectionHead title="Your games" onAll={() => navigate("/app/games")} />
          {recent.length === 0 ? (
            <EmptyState title="No games yet" text="Create your first AI-powered game." action={<Button onClick={() => navigate("/app/create")} className="rounded-full"><Plus className="w-4 h-4" /> Create</Button>} />
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {recent.map((g) => <GameCard key={g.id} game={{ ...g, world: g.config?.world }} onClick={() => open(g)} badge={g.status === "published" ? "LIVE" : "DRAFT"} />)}
            </div>
          )}
        </div>
        <div className="space-y-4">
          <div className="rounded-2xl border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[#1f6feb]/10 to-transparent p-5" data-testid="creator-ai-suggestions">
            <div className="font-display font-bold mb-2 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" /> AI suggestions</div>
            {a?.insights?.length ? (
              <ul className="space-y-2 text-sm text-[hsl(var(--muted-foreground))]">
                {a.insights.slice(0, 3).map((t, i) => <li key={i} className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span><span>{t}</span></li>)}
              </ul>
            ) : <p className="text-sm text-[hsl(var(--muted-foreground))]">Publish a game to unlock tailored growth tips from Nova.</p>}
          </div>
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
            <div className="font-display font-bold mb-1 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Game Jam</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">"Build a Space Game Weekend" is live!</p>
            <Button variant="outline" size="sm" className="rounded-full w-full" onClick={() => navigate("/app/arena")}>Join now</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const SectionHead = ({ title, onAll }) => (
  <div className="flex items-center justify-between mb-3">
    <h2 className="font-display text-xl font-bold">{title}</h2>
    {onAll && <button onClick={onAll} className="text-sm font-semibold text-[hsl(var(--primary))]">View all</button>}
  </div>
);
const ActionCard = ({ icon: Icon, title, text, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 card-hover">
    <div className="w-11 h-11 rounded-xl grid place-items-center bg-[hsl(var(--primary))]/15 mb-3"><Icon className="w-6 h-6 text-[hsl(var(--primary))]" /></div>
    <div className="font-display font-bold">{title}</div>
    <div className="text-sm text-[hsl(var(--muted-foreground))]">{text}</div>
  </button>
);
